import "../pages.scss";

import Table from "components/Table/Table.tsx";
import { SECTION } from "constants/SettingsConstants";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useEffect, useMemo, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import { setClaim } from "reducers/claims";
import {
  setCurrentSection,
  setIsLoading,
  setValidationErrors,
} from "reducers/main";
import { useGetClaimsQuery } from "services/claims-api";
import { fetchClaimAndUpdateStore } from "services/claimsDataSlice.ts";
import type { ClaimsDataType, ClaimsTableDataType } from "types/DataTypes";
import { useToken } from "utils/context.ts";
import { useQuery } from "utils/hooks";
import { useDispatch, useSelector } from "utils/redux";
import { GLOBAL_SEARCH_PARAM, useUrlStore } from "utils/useUrlStore.ts";

import ClaimsPageContainer from "./ClaimsPageContainer";

const columns = [
  { key: "id", name: "Claimant ID", ...commonColumnProps },
  { key: "icn", name: "ICN", ...commonColumnProps },
  {
    key: "injured_party_full_name",
    name: "Claimant Name",
    ...commonColumnProps,
  },
  {
    key: "is_medicare_beneficiary_verbose",
    name: "Beneficiary",
    ...commonColumnProps,
  },
  {
    key: "next_scheduled_action_verbose",
    name: "Next Action",
    ...commonColumnProps,
  },
  {
    key: "rre_name",
    name: "RRE",
    width: "15%",
  },
  {
    key: "client_name",
    name: "Client",
    width: "15%",
  },
  // {
  //   key: "has_cms_errors_to_resolve",
  //   name: "Reporting Errors?",
  //   ...commonColumnProps,
  //   renderCell: (_props: { row: ClaimsDataType }) => {
  //     return (<div>{_props.row.has_cms_errors_to_resolve ? "Yes" : "No"}</div>);
  //   },
  // },
  {
    key: "last_claim_send_date",
    name: "Last Claim Sent",
    ...commonColumnProps,
  },
  {
    key: "last_query_send_date",
    name: "Last Query Sent",
    ...commonColumnProps,
  },
  { key: "tags", name: "Tags" },
  //  the rre column is needed for filtering but shouldn't be displayed
  { key: "rre", name: "RRE ID", hidden: true },
];

const ClaimsTablePage = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accessToken } = useToken();
  const location = useLocation();
  const { getFilters } = useUrlStore();
  const filters = getFilters();
  const [page, setPage] = useState(1);

  const { data: claims = { results: [], count: 0 }, isLoading } =
    useGetClaimsQuery(
      {
        filters: {
          ...filters,
          limit: PAGE_SIZE.toString(),
          offset: OFFSET(page).toString(),
        },
        searchTerm: query.get(GLOBAL_SEARCH_PARAM),
      },
      { skip: false },
    );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  useEffect(() => {
    dispatch(setCurrentSection(SECTION.SECTION_111_CLAIMS));
    dispatch(setValidationErrors([]));
  }, [dispatch]);

  const visibleColumns = columns.filter((column) => column.hidden !== true);

  const rreIdToFilterBy = useSelector(
    (state) => state.claimsForm.rreIdToFilterBy,
  );

  // modified rows
  const modifiedRows: ClaimsTableDataType[] =
    useMemo((): ClaimsTableDataType[] => {
      const rows: ClaimsTableDataType[] = [];

      // Generate rows from the data
      claims.results?.map((claim: ClaimsDataType) => {
        const row: ClaimsTableDataType = {
          id: 0,
          icn: "",
          injured_party_full_name: "",
          is_medicare_beneficiary: null,
          is_medicare_beneficiary_verbose: "Unknown",
          next_scheduled_action_verbose: "",
          has_cms_errors_to_resolve: false,
          adjuster: "",
          tags: "",
          rre_name: "",
          client_name: "",
          rre: undefined,
        };

        columns.map((column) => {
          if (column.key === "tags") {
            row[column.key] = claim[column.key]
              .map((tag) => tag.name)
              .join(", ");
          } else {
            row[column.key] = claim[column.key];
          }
        });
        rows.push(row);
      });
      // if a filter exists, apply it
      const filteredRows = rows.filter((r) => {
        return rreIdToFilterBy ? rreIdToFilterBy === r.rre : r;
      });

      return filteredRows;
    }, [claims.results, rreIdToFilterBy]);

  // When a cell is clicked, go to the relevant page.
  // We are using history.push so that react-router can create the relevant route using url parameters
  const handleCellClick = (
    args: CellClickArgs<ClaimsTableDataType, unknown>,
  ) => {
    // get id and put it in store, update all routes to get it
    dispatch(
      fetchClaimAndUpdateStore({ accessToken, id: args.row.id.toString() }),
    );
    dispatch(setClaim(args.row));
    navigate(`${location.pathname}/${args.row.id}/overview`);
  };

  return (
    <ClaimsPageContainer>
      <Table
        className="claims-table"
        columns={visibleColumns}
        rows={modifiedRows}
        onCellClick={handleCellClick}
        rowKeyGetter={(row: ClaimsTableDataType) => row.id}
        fullHeight
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: claims.count,
          onPageChange: (pageIndex) => setPage(pageIndex),
          isLoading: isLoading,
        }}
      />
    </ClaimsPageContainer>
  );
};

export default ClaimsTablePage;
